import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { IMG_GRADE, PRODUCTS, cardUrl } from '@/components/riffle/data'
import { useReveal } from '@/hooks/use-reveal'

import {
    CARD_SHADOW,
    HAIRLINE,
    INK_BODY,
    INK_ERROR,
    INK_FAINT,
    INK_MUTED
} from '@/constants/riffle-ui'
import { contactEmail, pressEmail } from '@/constants/site'

type Status = 'idle' | 'sending' | 'sent'
type Field = 'name' | 'email' | 'subject' | 'message'

const EMPTY: Record<Field, string> = { name: '', email: '', subject: '', message: '' }
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const eyebrow = {
    fontSize: 11,
    letterSpacing: '0.32em',
    color: INK_MUTED
} as const

const fieldLabel = {
    fontSize: 10,
    letterSpacing: '0.26em',
    color: INK_FAINT
} as const

const note = {
    fontSize: 9.5,
    letterSpacing: '0.22em',
    color: INK_ERROR
} as const

// Kept out of the tab order and out of the accessibility tree — only a bot
// ever fills it in.
const honeypotStyle = {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    border: 0,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap'
} as const

const details: { label: string; value: string; href?: string }[] = [
    { label: 'General', value: contactEmail, href: `mailto:${contactEmail}` },
    { label: 'Press', value: pressEmail, href: `mailto:${pressEmail}` },
    { label: 'Studio', value: 'Unit 4, Hackney Wick, London E9 5EN' },
    { label: 'Hours', value: 'Monday to Friday, 9–6 GMT' }
]

// The card that sits beside the form — Nº08 out of the library.
const SAMPLE = 7

const Contact: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const firstRun = useRef(true)

    const [values, setValues] = useState<Record<Field, string>>(EMPTY)
    const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')
    const [honey, setHoney] = useState('')

    useReveal(rootRef)

    // Only the form/confirmation swap should re-play the entrance — going into
    // and out of "sending" leaves the same fields on screen.
    const showDone = status === 'sent'

    // The panel swaps long after the page reveal has run, so it owns its own
    // entrance — same curve, same stagger.
    useEffect(() => {
        const panel = panelRef.current
        if (!panel) return

        const items = panel.querySelectorAll<HTMLElement>('[data-stagger]')
        if (items.length === 0) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            gsap.set(items, { opacity: 1, y: 0 })
            return
        }

        const to = {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power4.out',
            stagger: 0.08
        }

        // First paint waits for the block to be scrolled to; every swap after
        // that is a direct response to a click, so it plays at once.
        if (firstRun.current) {
            firstRun.current = false
            gsap.registerPlugin(ScrollTrigger)

            const tween = gsap.fromTo(items, { opacity: 0, y: 24 }, {
                ...to,
                scrollTrigger: { trigger: panel, start: 'top 88%', once: true }
            })

            return () => {
                tween.scrollTrigger?.kill()
                tween.kill()
            }
        }

        const tween = gsap.fromTo(items, { opacity: 0, y: 24 }, to)

        return () => {
            tween.kill()
        }
    }, [showDone])

    const update =
        (field: Field) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
            const next = event.target.value
            setValues((prev) => ({ ...prev, [field]: next }))
            setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
        }

    const validate = (): Partial<Record<Field, string>> => {
        const found: Partial<Record<Field, string>> = {}
        if (!values.name.trim()) found.name = 'Tell us who you are'
        if (!EMAIL.test(values.email.trim())) found.email = 'A working email, please'
        if (values.message.trim().length < 10) found.message = 'A line or two more'
        return found
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        if (status === 'sending') return

        setFormError('')

        const found = validate()
        setErrors(found)
        if (Object.keys(found).length > 0) return

        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, company: honey })
            })

            const data = (await response.json()) as { ok: boolean; error?: string }

            if (!response.ok || !data.ok) {
                setStatus('idle')
                setFormError(data.error || 'That did not go through. Try again in a moment.')
                return
            }

            setValues(EMPTY)
            setErrors({})
            setStatus('sent')
        } catch {
            setStatus('idle')
            setFormError('That did not go through. Check your connection and try again.')
        }
    }

    const sending = status === 'sending'

    return (
        <>
            <Seo
                title='Contact'
                description='Stockist enquiries, press requests, or a note about an object you would like to see in the next edition of the Riffle library.'
            />

            <Layout>
                <div ref={rootRef} className='riffle-reveal'>
                    {/* ─── Hero — an indexed rail, set against the title ── */}
                    <section
                        id='contact-hero'
                        data-group
                        className='mx-auto flex w-full max-w-6xl items-center px-6 sm:px-12'
                        style={{ minHeight: 'clamp(480px, 78vh, 860px)' }}
                    >
                        <div className='grid w-full gap-8 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-0'>
                            {/* the rail — page index, held apart by a hairline */}
                            <div data-rev data-delay='0.15' className='flex items-baseline gap-4 sm:flex-col sm:gap-3'>
                                <span
                                    className='font-playfair'
                                    style={{ fontSize: 'clamp(1.75rem, 3.4vw, 2.6rem)', lineHeight: 1 }}
                                >
                                    Nº02
                                </span>

                                <span
                                    className='font-mono uppercase'
                                    style={{ fontSize: 9.5, letterSpacing: '0.26em', color: INK_FAINT }}
                                >
                                    Contact
                                </span>
                            </div>

                            <div className='sm:border-l sm:pl-12' style={{ borderColor: HAIRLINE }}>
                                <h1
                                    className='font-playfair overflow-hidden'
                                    style={{
                                        fontSize: 'clamp(3rem, 11vw, 9rem)',
                                        lineHeight: 1,
                                        letterSpacing: '-0.03em'
                                    }}
                                >
                                    <span
                                        data-title
                                        data-delay='0.24'
                                        className='inline-block'
                                        style={{ paddingBottom: '0.14em', willChange: 'transform' }}
                                    >
                                        Say hello.
                                    </span>
                                </h1>

                                <p
                                    data-rev
                                    className='mt-8 text-[15px] leading-[1.7] sm:mt-10 sm:text-[16.5px] sm:leading-[1.75]'
                                    style={{ maxWidth: 540, color: INK_BODY }}
                                >
                                    Stockist enquiries, press requests, or a note about an object you
                                    would like to see in the next edition. One of four people reads
                                    everything that lands here, usually the same afternoon.
                                </p>

                                <div
                                    data-rev
                                    className='font-mono mt-10 uppercase sm:mt-12'
                                    style={{ fontSize: 9.5, letterSpacing: '0.26em', color: INK_FAINT }}
                                >
                                    Reply within a day · Mon–Fri · 9–6 GMT
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── Form + details ────────────────────────── */}
                    <section
                        id='contact-form'
                        className='mx-auto w-full max-w-6xl px-6 pb-8 pt-6 sm:px-12 sm:pt-10'
                    >
                        <div style={{ height: 1, background: HAIRLINE }} />

                        <div className='grid gap-16 pt-10 sm:pt-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.75fr)] lg:gap-20'>
                            {/* form */}
                            <div data-group>
                                <div data-rev className='font-mono uppercase' style={eyebrow}>
                                    Write to us
                                </div>

                                <div ref={panelRef} className='mt-8'>
                                    {showDone ? (
                                        <div>
                                            <h2
                                                data-stagger
                                                className='font-playfair'
                                                style={{
                                                    fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                                                    lineHeight: 1.05,
                                                    letterSpacing: '-0.015em'
                                                }}
                                            >
                                                Thank you.
                                            </h2>

                                            <p
                                                data-stagger
                                                className='mt-4 text-[14.5px] leading-[1.7]'
                                                style={{ maxWidth: 460, color: INK_MUTED }}
                                            >
                                                Your message is with the studio. We answer in the
                                                order things arrive, so expect a reply within a
                                                working day.
                                            </p>

                                            <div data-stagger className='mt-8'>
                                                <button
                                                    type='button'
                                                    onClick={() => setStatus('idle')}
                                                    className='riffle-link font-mono uppercase'
                                                    style={{ fontSize: 10, letterSpacing: '0.28em' }}
                                                >
                                                    Send another →
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={onSubmit} noValidate>
                                            <div className='grid gap-8 sm:grid-cols-2'>
                                                <div data-stagger>
                                                    <label
                                                        htmlFor='name'
                                                        className='font-mono block uppercase'
                                                        style={fieldLabel}
                                                    >
                                                        Name
                                                    </label>

                                                    <input
                                                        id='name'
                                                        name='name'
                                                        type='text'
                                                        autoComplete='name'
                                                        className='riffle-field mt-2'
                                                        placeholder='Your name'
                                                        value={values.name}
                                                        onChange={update('name')}
                                                        aria-invalid={Boolean(errors.name)}
                                                        aria-describedby={errors.name ? 'name-note' : undefined}
                                                    />

                                                    {errors.name ? (
                                                        <p id='name-note' className='font-mono mt-2 uppercase' style={note}>
                                                            {errors.name}
                                                        </p>
                                                    ) : null}
                                                </div>

                                                <div data-stagger>
                                                    <label
                                                        htmlFor='email'
                                                        className='font-mono block uppercase'
                                                        style={fieldLabel}
                                                    >
                                                        Email
                                                    </label>

                                                    <input
                                                        id='email'
                                                        name='email'
                                                        type='email'
                                                        autoComplete='email'
                                                        className='riffle-field mt-2'
                                                        placeholder='you@studio.com'
                                                        value={values.email}
                                                        onChange={update('email')}
                                                        aria-invalid={Boolean(errors.email)}
                                                        aria-describedby={errors.email ? 'email-note' : undefined}
                                                    />

                                                    {errors.email ? (
                                                        <p id='email-note' className='font-mono mt-2 uppercase' style={note}>
                                                            {errors.email}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div data-stagger className='mt-8'>
                                                <label
                                                    htmlFor='subject'
                                                    className='font-mono block uppercase'
                                                    style={fieldLabel}
                                                >
                                                    Subject
                                                </label>

                                                <input
                                                    id='subject'
                                                    name='subject'
                                                    type='text'
                                                    className='riffle-field mt-2'
                                                    placeholder='Stockist, press, or something else'
                                                    value={values.subject}
                                                    onChange={update('subject')}
                                                />
                                            </div>

                                            <div data-stagger className='mt-8'>
                                                <label
                                                    htmlFor='message'
                                                    className='font-mono block uppercase'
                                                    style={fieldLabel}
                                                >
                                                    Message
                                                </label>

                                                <textarea
                                                    id='message'
                                                    name='message'
                                                    rows={5}
                                                    className='riffle-field mt-2'
                                                    placeholder='Tell us what you are after.'
                                                    value={values.message}
                                                    onChange={update('message')}
                                                    aria-invalid={Boolean(errors.message)}
                                                    aria-describedby={errors.message ? 'message-note' : undefined}
                                                />

                                                {errors.message ? (
                                                    <p id='message-note' className='font-mono mt-2 uppercase' style={note}>
                                                        {errors.message}
                                                    </p>
                                                ) : null}
                                            </div>

                                            <div aria-hidden style={honeypotStyle}>
                                                <label htmlFor='company'>Company</label>
                                                <input
                                                    id='company'
                                                    name='company'
                                                    type='text'
                                                    tabIndex={-1}
                                                    autoComplete='off'
                                                    value={honey}
                                                    onChange={(event) => setHoney(event.target.value)}
                                                />
                                            </div>

                                            <div
                                                data-stagger
                                                className='mt-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-7'
                                            >
                                                <button
                                                    type='submit'
                                                    disabled={sending}
                                                    className='riffle-order inline-flex shrink-0 items-center justify-center rounded-full px-7 py-3.5 text-[13px] font-semibold tracking-wide sm:px-8 sm:py-4'
                                                    style={{
                                                        background: 'rgba(255,255,255,0.97)',
                                                        color: '#0a0a0c',
                                                        boxShadow: '0 14px 36px -12px rgba(0,0,0,0.65)',
                                                        opacity: sending ? 0.55 : 1,
                                                        cursor: sending ? 'progress' : 'pointer'
                                                    }}
                                                >
                                                    <span
                                                        className='riffle-order-sheen pointer-events-none absolute inset-0'
                                                        aria-hidden
                                                        style={{
                                                            background:
                                                                'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)'
                                                        }}
                                                    />

                                                    <span style={{ position: 'relative' }}>
                                                        {sending ? 'Sending…' : 'Send message →'}
                                                    </span>
                                                </button>

                                                <p
                                                    className='font-mono uppercase'
                                                    style={{ fontSize: 9.5, letterSpacing: '0.26em', color: INK_FAINT }}
                                                >
                                                    Replies within one working day
                                                </p>
                                            </div>

                                            <div aria-live='polite'>
                                                {formError ? (
                                                    <p className='font-mono mt-6 uppercase' style={note}>
                                                        {formError}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>

                            {/* details */}
                            <div data-group>
                                <div data-rev className='font-mono uppercase' style={eyebrow}>
                                    Direct
                                </div>

                                <dl className='mt-8 space-y-7'>
                                    {details.map((item) => (
                                        <div key={item.label} data-rev>
                                            <dt
                                                className='font-mono uppercase'
                                                style={{ fontSize: 10, letterSpacing: '0.26em', opacity: 0.45 }}
                                            >
                                                {item.label}
                                            </dt>

                                            <dd className='mt-2 text-[14.5px] tracking-tight' style={{ opacity: 0.95 }}>
                                                {item.href ? (
                                                    <a href={item.href} className='riffle-link'>
                                                        {item.value}
                                                    </a>
                                                ) : (
                                                    item.value
                                                )}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>

                                <div data-rev className='mt-12'>
                                    <div
                                        className='relative overflow-hidden'
                                        style={{
                                            width: '100%',
                                            maxWidth: 260,
                                            aspectRatio: '1 / 1',
                                            borderRadius: 14,
                                            background: '#15151a',
                                            boxShadow: CARD_SHADOW
                                        }}
                                    >
                                        { /* eslint-disable-next-line @next/next/no-img-element */ }
                                        <img
                                            src={cardUrl(SAMPLE + 1)}
                                            alt={`${PRODUCTS[SAMPLE].title} — ${PRODUCTS[SAMPLE].artist}`}
                                            loading='lazy'
                                            draggable={false}
                                            className='absolute inset-0 h-full w-full object-cover'
                                            style={{ filter: IMG_GRADE }}
                                        />
                                    </div>

                                    <p
                                        className='font-mono mt-4 uppercase'
                                        style={{ fontSize: 9.5, letterSpacing: '0.26em', color: INK_FAINT }}
                                    >
                                        {`Nº0${SAMPLE + 1} ${PRODUCTS[SAMPLE].title} — on the sweep`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </Layout>
        </>
    )
}

export default Contact
